import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';

export interface PublishToolToOrgRequest {
  /** The tool's `weave_app_id` (its WorkflowTool id), sent in the URL; the server requires edit access to it. */
  weaveAppId: string;
  /** The target org's plan id; Sinatra authorizes the caller as a member of this plan and publishes there. */
  planId: string;
  /** The listing name; required (a blank name is rejected), and it also renames the tool's listing. */
  toolName: string;
  /** The frozen version from `prepareCommunityPublishSnapshot`, stored on the version this publish creates. */
  weavyVersionNumber: number;
  /** Uploaded image sha1s in display order; at least one is required, and position 0 is the listing thumbnail. */
  carouselSha1s: string[];
  /** The listing description; optional, and saved as given (empty when left out). */
  description?: string;
  /** The uploaded icon's sha1, validated as 40 hex characters; when left out, the previous icon is kept. */
  iconSha1?: string;
}

/** Who Figma credits as the publisher, as org members see it; absent until Sinatra returns it. */
export interface OrgPublisher {
  name: string;
  imgUrl?: string;
}

/** What an org publish returns; each field is null when the response doesn't carry it. */
export interface OrgPublishResult {
  publisher: OrgPublisher | null;
  /** Opens a new Figma Design file in the org with the tool open in its Tools panel. */
  openInFigmaUrl: string | null;
}

/** Sinatra's envelope; core relays it unchanged. */
interface OrgPublishResponseEnvelope {
  meta?: {
    publisher_name?: string | null;
    publisher_img_url?: string | null;
    open_in_figma_url?: string | null;
  };
}

/** Publishes a tool to one Figma org through core, which relays to Sinatra's org publish route. */
export async function publishToolToOrg(request: PublishToolToOrgRequest): Promise<OrgPublishResult> {
  const { data } = await safeAxios.post<OrgPublishResponseEnvelope>(
    API_ROUTES.figmaPublishing.toolOrgPublish({ weaveAppId: request.weaveAppId }),
    {
      plan_id: request.planId,
      tool_name: request.toolName,
      weavy_version_number: request.weavyVersionNumber,
      carousel_media: request.carouselSha1s.map((sha1, index) => ({ sha1, carousel_position: index })),
      ...(request.description ? { tool_description: request.description } : {}),
      ...(request.iconSha1 ? { tool_icon_sha1: request.iconSha1 } : {}),
    },
  );

  return { publisher: getOrgPublisher(data), openInFigmaUrl: data.meta?.open_in_figma_url || null };
}

function getOrgPublisher(data: OrgPublishResponseEnvelope): OrgPublisher | null {
  const name = data.meta?.publisher_name;
  if (!name) {
    return null;
  }
  const imgUrl = data.meta?.publisher_img_url;
  return imgUrl ? { name, imgUrl } : { name };
}
